// Global Studio tools — one Monitor, one Metronome and timeline Zoom for
// the whole session (track-level controls live on each lane / the Mixer).
import React from "react";
import { Headphones, Timer, ZoomIn, ZoomOut, Minus, Plus } from "lucide-react";

const Pill = ({ on, onClick, testid, children, title }) => (
  <button type="button" onClick={onClick} aria-pressed={on} title={title} data-testid={testid}
    className={`h-8 px-2.5 rounded-full border text-[11px] font-bold flex items-center gap-1 whitespace-nowrap transition-colors ${
      on ? "border-emerald-400/70 bg-emerald-500/15 text-emerald-200" : "border-white/15 text-gray-300 hover:border-white/30"}`}>
    {children}
  </button>
);

const Step = ({ onClick, testid, disabled, children, label }) => (
  <button type="button" onClick={onClick} disabled={disabled} aria-label={label} data-testid={testid}
    className="w-7 h-7 rounded-full border border-white/15 text-gray-200 flex items-center justify-center disabled:opacity-30 active:scale-95 transition-transform">
    {children}
  </button>
);

export default function StudioToolsBar({ monitor, onMonitor, monitorVol, onMonitorVol, metro, onMetro, bpmFromBeat, zoom, onZoom, zoomMin, zoomMax }) {
  return (
    <div className="flex items-center gap-1.5 px-2 py-1.5 overflow-x-auto border-b border-white/5" data-testid="studio-tools-bar" style={{ scrollbarWidth: "none" }}>
      <Pill on={monitor} onClick={() => onMonitor(!monitor)} testid="studio-monitor-btn" title="Hear your mic in headphones while recording">
        <Headphones className="w-3.5 h-3.5" />Mon<span className="hidden sm:inline">itor</span> {monitor ? "ON" : "OFF"}
      </Pill>
      {monitor && (
        <input type="range" min={0} max={1.5} step={0.05} value={monitorVol} onChange={(e) => onMonitorVol(parseFloat(e.target.value))}
          className="w-16 accent-emerald-400 shrink-0" aria-label="Monitor volume" data-testid="studio-monitor-volume" />
      )}
      <span className="w-px h-5 bg-white/10 shrink-0" />
      <Pill on={metro.on} onClick={() => onMetro({ on: !metro.on })} testid="studio-metronome-btn" title="Click track — never recorded or exported">
        <Timer className="w-3.5 h-3.5" />Metronome {metro.on ? "ON" : "OFF"}
      </Pill>
      <Step onClick={() => onMetro({ bpm: Math.max(40, metro.bpm - 1) })} testid="studio-bpm-down" label="Slower"><Minus className="w-3 h-3" /></Step>
      <span className="text-[11px] tabular-nums text-white font-bold whitespace-nowrap" data-testid="studio-bpm-value"
        title={bpmFromBeat ? "Starts at the beat's listed BPM" : "No BPM listed for this beat — default 90"}>
        {metro.bpm} BPM{!bpmFromBeat && <span className="hidden sm:inline text-gray-500 font-normal"> · set</span>}
      </span>
      <Step onClick={() => onMetro({ bpm: Math.min(240, metro.bpm + 1) })} testid="studio-bpm-up" label="Faster"><Plus className="w-3 h-3" /></Step>
      <span className="w-px h-5 bg-white/10 shrink-0" />
      <Step onClick={() => onZoom(-1)} disabled={zoom <= zoomMin} testid="studio-zoom-out" label="Zoom out"><ZoomOut className="w-3.5 h-3.5" /></Step>
      <span className="hidden sm:inline text-[10px] tabular-nums text-gray-400" data-testid="studio-zoom-value">{Math.round(zoom * 100)}%</span>
      <Step onClick={() => onZoom(1)} disabled={zoom >= zoomMax} testid="studio-zoom-in" label="Zoom in"><ZoomIn className="w-3.5 h-3.5" /></Step>
    </div>
  );
}
